import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

export interface RecurringBooking {
  id: string;
  client_id: string;
  workout_type_id: string;
  organization_id: string;
  day_of_week: string;
  time_slot: string;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    email?: string;
    paymentStatus?: string;
  };
}

export interface EffectiveBooking {
  booking_id: string | null;
  client_id: string;
  workout_type_id: string;
  time_slot: string;
  booking_type: 'regular' | 'recurring';
  recurring_booking_id: string | null;
  client_first_name: string;
  client_last_name: string;
  client_phone: string;
  client_email: string;
  client_payment_status: string;
  booking_status: string;
  session_consumption_type: string | null;
  session_consumption_id: string | null;
  trial_clients: Array<{
    id: string;
    full_name: string;
    booking_id: string;
  }> | null;
}

export const useRecurringBookings = () => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ['recurring-bookings', currentOrganization?.id],
    queryFn: async (): Promise<RecurringBooking[]> => {
      if (!currentOrganization) return [];

      const { data, error } = await supabase
        .from('recurring_bookings')
        .select(`
          *,
          clients!inner (
            id,
            first_name,
            last_name,
            phone,
            email,
            payment_status
          )
        `)
        .eq('organization_id', currentOrganization.id)
        .eq('is_active', true)
        .order('day_of_week')
        .order('time_slot');

      if (error) {
        console.error('Error fetching recurring bookings:', error);
        throw error;
      }

      return data?.map(booking => ({
        ...booking,
        client: {
          id: booking.clients.id,
          firstName: booking.clients.first_name,
          lastName: booking.clients.last_name,
          phone: booking.clients.phone,
          email: booking.clients.email,
          paymentStatus: booking.clients.payment_status,
        }
      })) || [];
    },
    enabled: !!currentOrganization?.id,
  });
};

export const useEffectiveBookingsForDate = (date: Date) => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ['effective-bookings', currentOrganization?.id, format(date, 'yyyy-MM-dd')],
    queryFn: async (): Promise<EffectiveBooking[]> => {
      if (!currentOrganization) return [];

      const { data, error } = await supabase.rpc('get_effective_bookings_for_date', {
        p_date: format(date, 'yyyy-MM-dd'),
        p_organization_id: currentOrganization.id,
      });

      if (error) {
        console.error('Error fetching effective bookings:', error);
        throw error;
      }

      return (data || []).map((item: any) => ({
        ...item,
        booking_type: item.booking_type as 'regular' | 'recurring'
      }));
    },
    enabled: !!currentOrganization?.id,
  });
};

export const useAddRecurringBooking = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clientId,
      workoutTypeId,
      dayOfWeek,
      timeSlot,
      startDate,
      endDate,
    }: {
      clientId: string;
      workoutTypeId: string;
      dayOfWeek: string;
      timeSlot: string;
      startDate?: Date;
      endDate?: Date;
    }) => {
      const { data, error } = await supabase.rpc('add_client_to_recurring_booking', {
        p_client_id: clientId,
        p_workout_type_id: workoutTypeId,
        p_day_of_week: dayOfWeek,
        p_time_slot: timeSlot,
        p_start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
        p_end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      });

      if (error) {
        console.error('Error creating recurring booking:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      toast.success(t('calendar.recurringBookingCreatedSuccessfully'));
    },
    onError: (error: any) => {
      console.error('Add recurring booking error:', error);
      let errorMessage = t('calendar.recurringBookingFailed');
      if (error.message?.includes('already has a recurring booking')) {
        errorMessage = t('calendar.clientAlreadyHasRecurringBooking');
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    },
  });
};

export const useCreateSessionConsumptionFromRecurring = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recurringBookingId,
      consumptionDate,
      consumptionType,
      notes,
    }: {
      recurringBookingId: string;
      consumptionDate: Date;
      consumptionType: 'completed' | 'missed';
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('create_session_consumption_from_recurring', {
        p_recurring_booking_id: recurringBookingId,
        p_consumption_date: format(consumptionDate, 'yyyy-MM-dd'),
        p_consumption_type: consumptionType,
        p_notes: notes || null,
      });

      if (error) {
        console.error('Error creating session consumption from recurring:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['session-consumptions'] });
      
      if (variables.consumptionType === 'completed') {
        toast.success(t('calendar.sessionMarkedCompleted'));
      } else if (variables.consumptionType === 'missed') {
        toast.success(t('calendar.sessionMarkedAbsent'));
      }
    },
    onError: (error: any) => {
      console.error('Create session consumption from recurring error:', error);
      toast.error(error.message || t('calendar.updateBookingFailed'));
    },
  });
};

export const useRemoveRecurringBookingSession = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recurringBookingId,
      sessionDate,
      removeAll = false,
      removeFromDate = false,
    }: {
      recurringBookingId: string;
      sessionDate: Date;
      removeAll?: boolean;
      removeFromDate?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('remove_recurring_booking_session', {
        p_recurring_booking_id: recurringBookingId,
        p_session_date: format(sessionDate, 'yyyy-MM-dd'),
        p_remove_all: removeAll,
        p_remove_from_date: removeFromDate,
      });

      if (error) {
        console.error('Error removing recurring booking session:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      
      if (variables.removeAll) {
        toast.success(t('calendar.recurringBookingRemovedSuccessfully'));
      } else if (variables.removeFromDate) {
        toast.success(t('calendar.recurringBookingEndDateSet'));
      } else {
        toast.success(t('calendar.sessionRemovedSuccessfully'));
      }
    },
    onError: (error: any) => {
      console.error('Remove recurring booking session error:', error);
      toast.error(error.message || t('calendar.removeSessionFailed'));
    },
  });
};